// import React, { useEffect, useMemo, useState } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService as API, DriverPerformance } from "../services/api";

/* ---------- Shimmer Skeleton Component ---------- */
const StatCardSkeleton = ({ iconBgColor }: { iconBgColor?: string }) => {
  return (
    <div className="group bg-white/70 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4 lg:px-4 lg:py-6 rounded-2xl border border-gray-300 overflow-hidden relative">
      <div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)'
        }}
      ></div>
      <div className="flex items-center justify-between relative">
        <div className="flex items-center space-x-4">
          <div className={`rounded-2xl p-4 ${iconBgColor || 'bg-gray-100'} opacity-30`}>
            <div className="w-6 h-6 bg-gray-300/50 rounded animate-pulse"></div>
          </div>
          <div className="flex-1">
            <div className="h-4 w-24 bg-gray-200/60 rounded mb-2 animate-pulse"></div>
            <div className="h-8 w-20 bg-gray-200/60 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `
      }} />
    </div>
  );
};

/* ---------- Filter Skeleton Component ---------- */
const FilterSkeleton = () => {
  return (
    <div className="relative bg-white/80 backdrop-blur-sm px-4 py-3 rounded-2xl border border-gray-300 mb-4 overflow-hidden">
      <div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)'
        }}
      ></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-7 gap-3 relative">
        {[...Array(4)].map((_, index) => (
          <div key={index}>
            <div className="h-3 w-16 bg-gray-200/60 rounded mb-2 animate-pulse"></div>
            <div className="h-10 w-full bg-gray-200/60 rounded animate-pulse"></div>
          </div>
        ))}
        <div className="flex items-end">
          <div className="h-10 w-20 bg-gray-200/60 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Chart Skeleton Component ---------- */
const ChartSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
      <div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)'
        }}
      ></div>
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 relative">
        <div className="h-6 w-48 bg-gray-200/60 rounded animate-pulse"></div>
      </div>
      <div className="p-6 relative">
        <div className="h-64 bg-gray-100/60 rounded animate-pulse"></div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  // Filter states
  const [dateRange, setDateRange] = useState('30'); // days
  const [vehicleType, setVehicleType] = useState('all');
  const [driverStatus, setDriverStatus] = useState('all');
  const [temperatureRange, setTemperatureRange] = useState('all');

  // Loaded data
  const [drivers, setDrivers] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [driverPerformance, setDriverPerformance] = useState<DriverPerformance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const normalizePhone = (num?: string) =>
    (num || '')
      .toString()
      .trim()
      .replace(/\s+/g, '')
      .replace(/^\+?91/, '')
      .replace(/^0+/, '');



  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // ✅ Single API call for all dashboard data
        const dashboardResp = await API.getVendorDashboard();

        if (dashboardResp.success && dashboardResp.data) {
          const dashboardData = dashboardResp.data;
          
          // Extract drivers, vehicles, and assignments from the response
          const driverList = Array.isArray(dashboardData.drivers) ? dashboardData.drivers : [];
          const vehicleList = Array.isArray(dashboardData.vehicles) ? dashboardData.vehicles : [];
          const assignmentList = Array.isArray(dashboardData.assignments) ? dashboardData.assignments : [];
          const topDriversList = Array.isArray(dashboardData.topDrivers) ? dashboardData.topDrivers : [];

          // ✅ Assign state
          setDrivers(driverList);
          setVehicles(vehicleList);
          setAssignments(assignmentList);
          setDriverPerformance(topDriversList);
        } else {
          console.warn('Dashboard API returned unsuccessful response:', dashboardResp);
          setDrivers([]);
          setVehicles([]);
          setAssignments([]);
          setDriverPerformance([]);
        }


      } catch (e) {
        console.error("Error loading dashboard data:", e);
        setDrivers([]);
        setVehicles([]);
        setAssignments([]);
        setDriverPerformance([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);


  // Custom date range states
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  const inDateRange = useCallback((createdAt?: string) => {
    if (!createdAt) return false;
    const d = new Date(createdAt);
    if (dateRange === 'custom') {
      const s = customStartDate ? new Date(customStartDate) : null;
      const e = customEndDate ? new Date(customEndDate) : null;
      if (s && d < s) return false;
      if (e && d > e) return false;
      return true;
    }
    const days = parseInt(dateRange, 10) || 30;
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - days + 1);
    return d >= start && d <= now;
  }, [dateRange, customStartDate, customEndDate]);

  // Apply filters effect
  useEffect(() => {
  }, [dateRange, vehicleType, driverStatus, temperatureRange, customStartDate, customEndDate]);

  // Handle date range change
  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    if (value === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setDateRange('30');
    setVehicleType('all');
    setDriverStatus('all');
    setTemperatureRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomDatePicker(false);
  };

  // Vendor driver scoping (if needed, filter by vendor's drivers present in system)
  const vendorDriverPhones = useMemo(() => new Set(
    drivers.map(d => normalizePhone((d as any).phoneNumber || (d as any).mobileNumber)).filter(Boolean)
  ), [drivers]);

  const phoneToDriver = useMemo(() => {
    const m = new Map<string, any>();
    drivers.forEach(d => {
      const ph = normalizePhone((d as any).phoneNumber || (d as any).mobileNumber);
      if (ph) m.set(ph, d);
    });
    return m;
  }, [drivers]);

  const vehicleNoToType = useMemo(() => {
    const m = new Map<string, string>();
    vehicles.forEach(v => {
      const no = (v as any).vehicleNumber;
      const type = (v as any).vehicleType || (v as any).type || '';
      if (no) m.set(no, String(type).toLowerCase());
    });
    return m;
  }, [vehicles]);

  // Generate a consistent color for each vehicle type
  const getColorForVehicleType = useCallback((type: string) => {
    const colors = ['#3b82f6', '#10b981', '#ef4444', '#f97316', '#a855f7', '##eab308', '#6b7280'];
    let hash = 0;
    for (let i = 0; i < type.length; i++) {
      hash = type.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  }, []);

  const vehicleTypeDistribution = useMemo(() => {
    const distributionMap = new Map<string, number>();
    vehicles.forEach(v => {
      const type = (v as any).vehicleType || (v as any).type || 'Unknown';
      const normalizedType = String(type).toLowerCase();
      distributionMap.set(normalizedType, (distributionMap.get(normalizedType) || 0) + 1);
    });
    
    const total = vehicles.length;
    let startAngle = 0;
    const result = Array.from(distributionMap.entries()).map(([type, count]) => {
      const percentage = total ? (count / total) * 100 : 0;
      const endAngle = startAngle + (percentage / 100) * 360;
      const color = getColorForVehicleType(type); 
      const entry = { type, count, percentage, startAngle, endAngle, color };
      startAngle = endAngle;
      return entry;
    });
    return result;
  }, [vehicles, getColorForVehicleType]);

  // Filter options
  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const vehicleTypeOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Vehicles' }];
    vehicleTypeDistribution.forEach(d => {
      options.push({ value: d.type, label: d.type.charAt(0).toUpperCase() + d.type.slice(1) });
    });
    return options;
  }, [vehicleTypeDistribution]);

  const driverStatusOptions = [
    { value: 'all', label: 'All Drivers' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const temperatureRangeOptions = [
    { value: 'all', label: 'All Temperatures' },
    { value: 'cold', label: 'Cold Chain (0-4°C)' },
    { value: 'frozen', label: 'Frozen (-18°C)' },
    { value: 'ambient', label: 'Ambient (15-25°C)' }
  ];

  const scopedAssignments = useMemo(() =>
    assignments.filter(a => {
      const phone = normalizePhone(a?.driverNumber);
      return vendorDriverPhones.size ? vendorDriverPhones.has(phone) : true;
    }), [assignments, vendorDriverPhones]
  );

  const filteredAssignments = useMemo(() => {
    return scopedAssignments.filter(a => {
      if (!inDateRange(a?.createdAt)) return false;
      // Vehicle type filter
      if (vehicleType !== 'all') {
        const vt = vehicleNoToType.get(a?.vehicleNumber || '') || '';
        if (vt !== vehicleType) return false;
      }
      // Driver status filter
      if (driverStatus !== 'all') {
        const drv = phoneToDriver.get(normalizePhone(a?.driverNumber));
        const st = (drv?.status || drv?.isActive === false ? 'Inactive' : 'Active');
        if (driverStatus === 'active' && st !== 'Active') return false;
        if (driverStatus === 'inactive' && st !== 'Inactive') return false;
      }
      // Temperature filter
      if (temperatureRange !== 'all') {
        const tempReq = !!a?.tempRequired;
        const tmin = typeof a?.tempMin === 'number' ? a.tempMin : Number.NEGATIVE_INFINITY;
        // const tmax = typeof a?.tempMax === 'number' ? a.tempMax : Number.POSITIVE_INFINITY;
        if (temperatureRange === 'cold' && !(tempReq)) return false; // assume cold == temp controlled
        if (temperatureRange === 'frozen' && !(tempReq && tmin <= -18)) return false;
        if (temperatureRange === 'ambient' && tempReq) return false;
      }
      return true;
    });
    // }, [scopedAssignments, dateRange, customStartDate, customEndDate, vehicleType, vehicleNoToType, driverStatus, phoneToDriver, temperatureRange]);
  }, [scopedAssignments, vehicleType, vehicleNoToType, driverStatus, phoneToDriver, temperatureRange, inDateRange]);

  const totalDrivers = useMemo(() => drivers.length, [drivers]);
  const temperatureViolations = useMemo(() => filteredAssignments.filter(a => !!a.tempAbuse).length, [filteredAssignments]);

  // Delivery metrics calculations
  const ongoingDeliveries = useMemo(() => {
    return filteredAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return status === 'ongoing' || status === 'loading_vehicle' || status === 'pickup_point' || status === 'close';
    }).length;
  }, [filteredAssignments]);

  const totalDeliveries = useMemo(() => filteredAssignments.length, [filteredAssignments]);

  const deliveredDeliveries = useMemo(() => {
    return filteredAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return a.deliveryCompleted === true || 
             status === 'delivered' || 
             status === 'completedbyadmin' ||
             a.status === 'completed';
    }).length;
  }, [filteredAssignments]);

  const upcomingDeliveries = useMemo(() => {
    return filteredAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return status === 'upcoming' || status === 'pending';
    }).length;
  }, [filteredAssignments]);

  // Calculate assigned vehicles (vehicles with active assignments)
  const assignedVehicles = useMemo(() => {
    const activeAssignments = filteredAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return status !== 'delivered' && 
             status !== 'completedbyadmin' && 
             status !== 'cancelled' &&
             a.deliveryCompleted !== true;
    });
    
    // Get unique vehicle numbers from active assignments
    const uniqueVehicles = new Set(
      activeAssignments
        .map(a => a.vehicleNumber || a.vehicleId?.vehicleNumber || '')
        .filter(Boolean)
    );
    
    return uniqueVehicles.size;
  }, [filteredAssignments]);

  const metrics = useMemo(() => ([
    { title: 'Total Deliveries', value: String(totalDeliveries), icon: '📦', color: 'text-purple-500', trend: undefined, subtitle: undefined },
    { title: 'Total Vehicles', value: String(vehicles.length), icon: '🚛', color: 'text-jd-green', trend: undefined, subtitle: `${assignedVehicles} assigned` },
    { title: 'Ongoing Deliveries', value: String(ongoingDeliveries), icon: '🚚', color: 'text-blue-600', trend: undefined, subtitle: undefined },
    { title: 'Delivered Deliveries', value: String(deliveredDeliveries), icon: '✅', color: 'text-green-600', trend: undefined, subtitle: undefined },
    { title: 'Upcoming Deliveries', value: String(upcomingDeliveries), icon: '📅', color: 'text-orange-500', trend: undefined, subtitle: undefined },
  ]), [totalDeliveries, vehicles.length, assignedVehicles, ongoingDeliveries, deliveredDeliveries, upcomingDeliveries]);

  // Driver status distribution
  const activeDrivers = useMemo(() => drivers.filter(d => (d as any).status === 'Active').length, [drivers]);
  const inactiveDrivers = useMemo(() => drivers.filter(d => (d as any).status === 'Inactive').length, [drivers]);
  const activePct = useMemo(() => totalDrivers ? Math.round((activeDrivers / totalDrivers) * 100) : 0, [activeDrivers, totalDrivers]);
  const inactivePct = useMemo(() => 100 - activePct, [activePct]);

  // Success rate
  const completedAssignments = useMemo(() => filteredAssignments.filter(a => a.deliveryCompleted === true || a.deliveryStatus === 'delivered' || a.deliveryStatus === 'completedByAdmin' || a.status === 'completed'), [filteredAssignments]);
  const successRate = useMemo(() => {
    const total = filteredAssignments.length;
    if (!total) return 0;
    return Math.round((completedAssignments.length / total) * 100);
  }, [filteredAssignments, completedAssignments]);

  // Delivery trends (counts per day)
  const trendsPoints = useMemo(() => {
    const map = new Map<string, number>();
    filteredAssignments.forEach(a => {
      const d = a.createdAt ? new Date(a.createdAt) : null;
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    const keys = Array.from(map.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    return keys.map((k, idx) => ({ x: 20 + idx * 40, y: 180 - Math.min(140, (map.get(k) || 0) * 10), value: map.get(k) || 0 }));
  }, [filteredAssignments]);

  // Violations by vehicle
  const violationsByVehicle = useMemo(() => {
    const vm = new Map<string, number>();
    filteredAssignments.forEach(a => {
      if (a.tempAbuse) vm.set(a.vehicleNumber, (vm.get(a.vehicleNumber) || 0) + 1);
    });
    const entries = Array.from(vm.entries()).slice(0, 6);
    return entries.map(([vehicle, violations]) => ({ vehicle, violations, color: violations > 0 ? 'bg-red-500' : 'bg-green-400' }));
  }, [filteredAssignments]);


  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-full">
      {/* Page Title */}
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Monitor your temperature-controlled logistics operations</p>
      </div>

      {/* Filters Section */}
      {loading ? (
        <FilterSkeleton />
      ) : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900">Filters</h3>
          </div>

          <div className="flex flex-wrap gap-3">
            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue bg-white"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Vehicle Type Filter */}
            <select
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue bg-white"
            >
              {vehicleTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Driver Status Filter */}
            <select
              value={driverStatus}
              onChange={(e) => setDriverStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue bg-white"
            >
              {driverStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Temperature Range Filter */}
            <select
              value={temperatureRange}
              onChange={(e) => setTemperatureRange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue bg-white"
            >
              {temperatureRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Reset Button */}
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomDatePicker && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue"
                placeholder="End Date"
              />
              <button
                onClick={() => setShowCustomDatePicker(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-jd-blue focus:border-jd-blue transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Active Filters Display - Only show if any filters are active */}
        {(dateRange !== '30' || vehicleType !== 'all' || driverStatus !== 'all' || temperatureRange !== 'all') && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {dateRange !== '30' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {dateRangeOptions.find(opt => opt.value === dateRange)?.label}
                  <button
                    onClick={() => setDateRange('30')}
                    className="ml-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full text-blue-500 hover:bg-blue-200 hover:text-blue-700"
                  >
                    ×
                  </button>
                </span>
              )}
              {vehicleType !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                  {vehicleTypeOptions.find(opt => opt.value === vehicleType)?.label}
                  <button
                    onClick={() => setVehicleType('all')}
                    className="ml-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full text-green-500 hover:bg-green-200 hover:text-green-700"
                  >
                    ×
                  </button>
                </span>
              )}
              {driverStatus !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  {driverStatusOptions.find(opt => opt.value === driverStatus)?.label}
                  <button
                    onClick={() => setDriverStatus('all')}
                    className="ml-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full text-purple-500 hover:bg-purple-200 hover:text-purple-700"
                  >
                    ×
                  </button>
                </span>
              )}
              {temperatureRange !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  {temperatureRangeOptions.find(opt => opt.value === temperatureRange)?.label}
                  <button
                    onClick={() => setTemperatureRange('all')}
                    className="ml-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full text-orange-500 hover:bg-orange-200 hover:text-orange-700"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-4 mb-4 sm:mb-4">
        {loading ? (
          <>
            <StatCardSkeleton iconBgColor="bg-purple-100" />
            <StatCardSkeleton iconBgColor="bg-green-100" />
            <StatCardSkeleton iconBgColor="bg-blue-100" />
            <StatCardSkeleton iconBgColor="bg-emerald-100" />
            <StatCardSkeleton iconBgColor="bg-orange-100" />
          </>
        ) : (
          metrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1 truncate">{metric.title}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">{metric.value}</p>
                  {metric.subtitle && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">{metric.subtitle}</p>
                  )}
                </div>
                <div className="flex flex-col items-end ml-2">
                  <div className={`text-2xl sm:text-3xl ${metric.color} mb-1 sm:mb-2`}>
                    {metric.icon}
                  </div>
                  {metric.trend && (
                    <div className="flex items-center text-jd-blue text-xs sm:text-sm">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      {metric.trend}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Analytics Charts Section */}
      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
        </div>
      ) : (
      <div className="space-y-4">
        {/* First Row - Line Chart and Pie Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Delivery Trends Line Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Delivery Trends ({dateRange === 'custom' ? `${customStartDate || 'from'} to ${customEndDate || 'now'}` : dateRangeOptions.find(opt => opt.value === dateRange)?.label})
              </h2>
            </div>
            <div className="p-6">
              <div className="h-64 relative">
                <svg className="w-full h-full" viewBox="0 0 400 200">
                  {/* Grid lines */}
                  <defs>
                    <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#f3f4f6" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />


                  <polyline
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    points={(trendsPoints.length ? trendsPoints : [{ x: 20, y: 180, value: 0 }]).map(p => `${p.x},${p.y}`).join(' ')}
                  />

                  {/* Data points */}
                  {(trendsPoints.length ? trendsPoints : [{ x: 20, y: 180, value: 0 }]).map((point, index) => (
                    <g key={index}>
                      <circle cx={point.x} cy={point.y} r="4" fill="#3b82f6" />
                      <text x={point.x} y={point.y - 10} textAnchor="middle" className="text-xs fill-gray-600">
                        {point.value}
                      </text>
                    </g>
                  ))}

                  {/* Y-axis labels */}
                  <text x="10" y="30" className="text-xs fill-gray-500">250</text>
                  <text x="10" y="80" className="text-xs fill-gray-500">200</text>
                  <text x="10" y="130" className="text-xs fill-gray-500">150</text>
                  <text x="10" y="180" className="text-xs fill-gray-500">100</text>
                </svg>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  📈 +15% growth {dateRange === '7' ? 'this week' : dateRange === '30' ? 'this month' : dateRange === '90' ? 'this quarter' : dateRange === '365' ? 'this year' : 'in selected period'}
                </p>
              </div>
            </div>
          </div>

          {/* Driver Status Pie Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Driver Status Distribution</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-48">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Active drivers dynamic */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="8"
                      strokeDasharray={`${activePct * 2.51} 251`}
                      strokeDashoffset="0"
                    />
                    {/* Inactive drivers dynamic */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="8"
                      strokeDasharray={`${inactivePct * 2.51} 251`}
                      strokeDashoffset={`-${activePct * 2.51}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{totalDrivers}</div>
                      <div className="text-sm text-gray-500">Total</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-700">Active</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{activeDrivers} ({totalDrivers ? Math.round((activeDrivers / totalDrivers) * 100) : 0}%)</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-gray-700">Inactive</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{inactiveDrivers} ({totalDrivers ? Math.round((inactiveDrivers / totalDrivers) * 100) : 0}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Second Row - Donut Chart and Bar Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Delivery Success Rate Donut Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Delivery Success Rate</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-48">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Success rate */}
                    <circle
                      cx="50"
                      cy="50"
                      r="35"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="6"
                      strokeDasharray={`${Math.max(0, Math.min(100, successRate)) * 2.2} 220`}
                      strokeDashoffset="0"
                    />
                    {/* Failure rate */}
                    <circle
                      cx="50"
                      cy="50"
                      r="35"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="6"
                      strokeDasharray={`${(100 - Math.max(0, Math.min(100, successRate))) * 2.2} 220`}
                      strokeDashoffset={`-${Math.max(0, Math.min(100, successRate)) * 2.2}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">{successRate}%</div>
                      <div className="text-sm text-gray-500">Success</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{completedAssignments.length}</div>
                  <div className="text-xs text-green-700">Successful</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <div className="text-lg font-bold text-red-600">{Math.max(filteredAssignments.length - completedAssignments.length, 0)}</div>
                  <div className="text-xs text-red-700">Failed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Temperature Violations Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Temperature Violations by Vehicle</h2>
            </div>
            <div className="p-6">
              <div className="h-64 flex items-end justify-between space-x-4">
                {(violationsByVehicle.length ? violationsByVehicle : [{ vehicle: 'N/A', violations: 0, color: 'bg-green-400' }]).map((item, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div
                      className={`${item.color} rounded-t w-full transition-all duration-300 hover:opacity-80`}
                      style={{ height: `${Math.max(item.violations * 40, 20)}px` }}
                    ></div>
                    <div className="text-xs text-gray-500 mt-2 text-center">
                      {item.vehicle ? String(item.vehicle).slice(-4) : 'N/A'}
                    </div>
                    <div className="text-xs font-medium text-gray-700 mt-1">{item.violations}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  Total violations: {temperatureViolations} {dateRange === '7' ? 'this week' : dateRange === '30' ? 'this month' : dateRange === '90' ? 'this quarter' : dateRange === '365' ? 'this year' : 'in selected period'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Third Row - Vehicle Type Pie Chart and Driver Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Vehicle Type Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Vehicle Type Distribution</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-48">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Dynamic Vehicle type arcs */}
                    {vehicleTypeDistribution.map((entry, index) => (
                      <circle
                        key={entry.type}
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke={entry.color}
                        strokeWidth="8"
                        strokeDasharray={`${entry.percentage * 2.51} 251`}
                        strokeDashoffset={`-${(entry.startAngle / 360) * 251}`}
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{vehicles.length}</div>
                      <div className="text-sm text-gray-500">Vehicles</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {vehicleTypeDistribution.map((entry) => (
                  <div key={entry.type} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                      <span className="text-sm text-gray-700">{entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{entry.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Driver Performance Radar-style Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Top Driver Performance</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {driverPerformance.map((driver, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 ${driver.color} rounded-full flex items-center justify-center text-white text-lg font-bold`}>
                        {driver.name[0]}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{driver.name}</div>
                        <div className="text-sm text-gray-500">{driver.deliveries} deliveries</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-yellow-500">★ {driver.rating}</div>
                        <div className="text-xs text-gray-500">Rating</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{driver.efficiency}%</div>
                        <div className="text-xs text-gray-500">Efficiency</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

    </div>
  );
};

export default Dashboard;